<script setup>
import { ref } from 'vue'
import { router } from '@inertiajs/vue3'
import { useI18n } from 'vue-i18n'
import AppLayout from '@/Layouts/AppLayout.vue'
import PageShell from '@/Components/UI/PageShell.vue'
import UiButton from '@/Components/UI/UiButton.vue'

defineOptions({ layout: AppLayout })
const { t } = useI18n()

const props = defineProps({
  pending:  { type: Array, default: () => [] },
  rejected: { type: Array, default: () => [] }
})

const rejectingId      = ref(null)
const rejectionReason  = ref('')

function approve(property) {
  if (!confirm(t('property_approvals.approve_confirm', { title: property.title }))) return
  router.patch(`/admin/property_approvals/${property.id}/approve`, {}, { preserveScroll: true })
}
function startReject(property) {
  rejectingId.value = property.id
  rejectionReason.value = ''
}
function submitReject(property) {
  router.patch(`/admin/property_approvals/${property.id}/reject`, { reason: rejectionReason.value }, {
    preserveScroll: true,
    onSuccess: () => { rejectingId.value = null }
  })
}
</script>

<template>
  <PageShell :title="t('property_approvals.title')">
    <template #subtitle>
      <p class="text-sm text-[color:var(--inkSoft)]">{{ t('property_approvals.subtitle') }}</p>
    </template>

    <section class="space-y-3">
      <h2 class="section-title">{{ t('property_approvals.sections.pending', { n: pending.length }) }}</h2>

      <p v-if="pending.length === 0" class="empty">{{ t('property_approvals.empty_pending') }}</p>

      <div v-for="p in pending" :key="p.id" class="card">
        <div class="card__main">
          <img v-if="p.photo_url" :src="p.photo_url" :alt="p.title" class="thumb" />
          <div v-else class="thumb thumb--blank">{{ p.title?.[0] }}</div>

          <div class="info">
            <div class="info__title">
              <span class="info__link">{{ p.title }}</span>
            </div>
            <div v-if="p.host" class="info__supplier">
              {{ t('property_approvals.from', { name: p.host.name }) }}
            </div>
            <div class="info__terms">
              {{ p.category }} · {{ p.area }} · {{ p.price_per_night }}
            </div>
          </div>

          <div class="actions">
            <UiButton size="sm" variant="primary" @click="approve(p)">{{ t('property_approvals.approve') }}</UiButton>
            <UiButton size="sm" variant="ghost" @click="startReject(p)">{{ t('property_approvals.reject') }}</UiButton>
          </div>
        </div>

        <div v-if="rejectingId === p.id" class="reject-form">
          <input
            v-model="rejectionReason"
            type="text"
            :placeholder="t('property_approvals.reject_reason_placeholder')"
            class="reject-input"
          />
          <UiButton size="sm" variant="danger" @click="submitReject(p)">{{ t('property_approvals.confirm_reject') }}</UiButton>
          <UiButton size="sm" variant="ghost"  @click="rejectingId = null">{{ t('common.cancel') }}</UiButton>
        </div>
      </div>
    </section>

    <section v-if="rejected.length" class="space-y-3 mt-8">
      <h2 class="section-title">{{ t('property_approvals.sections.rejected', { n: rejected.length }) }}</h2>
      <p class="rejected-note">{{ t('property_approvals.rejected_note') }}</p>
      <div v-for="p in rejected" :key="p.id" class="card card--rejected">
        <div class="card__main">
          <div class="info">
            <div class="info__title">
              <span class="info__link">{{ p.title }}</span>
            </div>
            <div v-if="p.host" class="info__supplier">{{ p.host.name }}</div>
            <div v-if="p.rejection_reason" class="info__reason">{{ p.rejection_reason }}</div>
          </div>
        </div>
      </div>
    </section>
  </PageShell>
</template>

<style scoped>
.section-title {
  font-size: 0.75rem; text-transform: uppercase; letter-spacing: 0.05em;
  color: var(--muted); margin: 0;
}
.empty {
  padding: 1.5rem; text-align: center; color: var(--muted);
  border: 1px dashed var(--line); border-radius: var(--radius-md, 8px);
}
.card {
  background: var(--surface);
  border: 1px solid var(--line);
  border-radius: var(--radius-md, 8px);
  padding: 0.85rem 1rem;
}
.card--rejected { opacity: 0.85; border-style: dashed; }
.card__main { display: flex; gap: 1rem; align-items: center; }
.thumb {
  width: 4.5rem; height: 3.5rem; object-fit: cover;
  border-radius: 0.5rem; background: var(--surfaceAlt);
  flex-shrink: 0;
}
.thumb--blank {
  display: grid; place-items: center;
  font-weight: 700; color: var(--muted);
  font-family: var(--font-display, var(--display));
}
.info { flex: 1; min-width: 0; }
.info__title { font-weight: 600; color: var(--ink); }
.info__link { color: var(--ink); text-decoration: none; }
.info__link:hover { color: var(--accent); }
.info__supplier, .info__terms { font-size: 0.8125rem; color: var(--inkSoft); }
.info__reason { font-size: 0.8125rem; color: var(--danger); }
.actions { display: flex; gap: 0.4rem; flex-shrink: 0; }
.rejected-note { font-size: 0.8125rem; color: var(--muted); margin: 0 0 0.25rem; }

.reject-form {
  display: flex; gap: 0.4rem; margin-top: 0.6rem;
}
.reject-input {
  flex: 1; padding: 0.4rem 0.6rem;
  background: var(--surface); border: 1px solid var(--line);
  border-radius: var(--radius-input, 6px); color: var(--ink); font-size: 0.875rem;
}
.reject-input:focus { outline: 2px solid var(--accent); outline-offset: 1px; }
</style>
